// MIDAS V3 — Machine status mapping helper (R3 Path A)
//
// SPEC: MIDAS_V3_BUGS_0519_R3_SPEC.md sub-task 3.
//
// Single source of truth that maps a machine's `activeServiceRequestCount`
// (Path A — SR-count semantics) to the live-PLC vocabulary the UI should
// display. Locking the vocabulary now means that when Path B
// (MIDAS-V3-LIVE-STATUS-BACKEND) lands, only the BODY of this function needs
// to change — every call site already displays "Running / Stopped / Idle"
// and the visual transition for users will be invisible (same labels, real
// telemetry).
//
// Mapping (Path A):
//   • activeServiceRequestCount > 0     → Stopped (red)    — open SR ⇒ operationally stopped
//   • activeServiceRequestCount === 0   → Running (green)  — operating normally
//   • activeServiceRequestCount missing → Idle    (grey)   — present but unknown
//
// Why "Stopped" not "Alarm" for SR>0: live-PLC vocabulary includes both
// `Stopped` and `Alarm` — SR-count can't distinguish them. Using the broader
// "Stopped" lets Path B later split Alarm out without contradicting Path A.
//
// Path B (future) extension point — the obvious one-line change:
//   export function mapMachineStatus(
//     args: { srCount?: number; liveTelemetry?: LivePlcStatus | null }
//   ): MachineStatusVisual { ... }
// (or simply replace this function's body to consult liveTelemetry first
//  and fall through to the SR-count path when there's no edge agent).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.

export type MachineStatusLabel = "Running" | "Stopped" | "Idle";

export interface MachineStatusVisual {
  /** Canonical label key — UI maps this to i18n via `machineStatus.{label.toLowerCase()}`. */
  label: MachineStatusLabel;
  /** CSS variable reference for the foreground/text color in the pill. */
  color: string;
  /** CSS variable reference for the small dot pip. Kept separate from
   *  `color` so Path B can use an outlined/desaturated pip for `Offline`. */
  pipColor: string;
}

/** Path A — derive status purely from SR count. Single swap point for Path B. */
export function mapSrCountToStatus(
  activeServiceRequestCount: number | null | undefined
): MachineStatusVisual {
  if (activeServiceRequestCount === undefined || activeServiceRequestCount === null) {
    return {
      label: "Idle",
      color: "var(--midas-v3-amber)",
      pipColor: "var(--midas-v3-amber)",
    };
  }
  if (activeServiceRequestCount > 0) {
    return {
      label: "Stopped",
      color: "var(--midas-v3-red)",
      pipColor: "var(--midas-v3-red)",
    };
  }
  return {
    label: "Running",
    color: "var(--midas-v3-green)",
    pipColor: "var(--midas-v3-green)",
  };
}
